import { DialogComponent } from '@theme/dialog';
import { CartAddEvent, ThemeEvents } from '@theme/events';

/**
 * A custom element that manages a cart drawer.
 *
 * @extends {DialogComponent}
 */
class CartDrawerComponent extends DialogComponent {
  connectedCallback() {
    super.connectedCallback();
    document.addEventListener(CartAddEvent.eventName, this.#handleCartAdd);
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    document.removeEventListener(CartAddEvent.eventName, this.#handleCartAdd);
  }

  #handleCartAdd = () => {
    if (this.hasAttribute('auto-open')) {
      this.showDialog();
    }
  };

  open() {
    this.showDialog();

    /**
     * Close cart drawer when installments CTA is clicked to avoid overlapping dialogs
     */
    customElements.whenDefined('shopify-payment-terms').then(() => {
      const installmentsContent = document.querySelector('shopify-payment-terms')?.shadowRoot;
      const cta = installmentsContent?.querySelector('#shopify-installments-cta');
      cta?.addEventListener('click', this.closeDialog, { once: true });
    });
  }

  close() {
    this.closeDialog();
  }
}

if (!customElements.get('cart-drawer-component')) {
  customElements.define('cart-drawer-component', CartDrawerComponent);
}

/**
 * Session-wide deadline for the "Items reserved for" timer, so the countdown carries on across page loads. Falls back
 * to memory when storage is blocked (private mode, disabled site data).
 */
const reserveDeadline = {
  key: 'cartReserveDeadline',
  memory: 0,
  get() {
    try {
      return Number(window.sessionStorage.getItem(this.key)) || 0;
    } catch {
      return this.memory;
    }
  },
  /** @param {number} value */
  set(value) {
    this.memory = value;
    try {
      window.sessionStorage.setItem(this.key, String(value));
    } catch {}
  },
  clear() {
    this.memory = 0;
    try {
      window.sessionStorage.removeItem(this.key);
    } catch {}
  },
};

/**
 * The "Items reserved for m:ss" strip in the cart drawer and the cart page, rendered only when the theme setting is on.
 * Shopify does not hold stock for carts: this is a countdown only. It starts the first time a cart with items is shown in
 * this browser session, hides at 0:00, and starts again after the cart has been emptied. Both mark it
 * data-skip-node-update and data-skip-subtree-update so cart morphs leave it alone.
 *
 * @extends {HTMLElement}
 */
class CartReserveTimer extends HTMLElement {
  #interval = 0;

  connectedCallback() {
    if (!reserveDeadline.get()) {
      const minutes = Number(this.dataset.minutes) || 10;
      reserveDeadline.set(Date.now() + minutes * 60_000);
    }
    this.#tick();
    this.#interval = window.setInterval(this.#tick, 1000);
  }

  disconnectedCallback() {
    window.clearInterval(this.#interval);
  }

  #tick = () => {
    const left = Math.max(0, Math.ceil((reserveDeadline.get() - Date.now()) / 1000));
    this.hidden = left === 0;
    if (left === 0) {
      window.clearInterval(this.#interval);
      return;
    }
    const time = this.querySelector('[data-cdz-time]');
    if (time) time.textContent = `${Math.floor(left / 60)}:${String(left % 60).padStart(2, '0')}`;
  };
}

if (!customElements.get('cart-reserve-timer')) {
  customElements.define('cart-reserve-timer', CartReserveTimer);
}

// A fresh countdown starts once the cart has been emptied, in this page or before it loaded. The item count comes from
// whichever cart this page renders: the drawer's, or the cart page's (which loads this module without a drawer).
document.addEventListener(ThemeEvents.cartUpdate, (event) => {
  if (/** @type {any} */ (event).detail?.data?.itemCount === 0) reserveDeadline.clear();
});
if (document.querySelector('cart-items-component [ref="cartItemCount"]')?.textContent?.trim() === '0') {
  reserveDeadline.clear();
}
